import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { ARCANA_ORDER, CARDS, HUB_PATHS, MEANING_LANGS, cardPath } from '../../data/cardMeanings';
import STRINGS from './strings';
import './CardMeanings.css';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';

const ORIGIN = 'https://nowtarot.com';

const CardMeaningsHub = ({ lang = 'en' }) => {
  const strings = STRINGS[lang];
  const canonical = `${ORIGIN}${HUB_PATHS[lang]}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: strings.hubHeading,
    description: strings.hubDescription,
    url: canonical,
    inLanguage: lang,
    hasPart: CARDS.map((card) => ({
      '@type': 'Article',
      name: card.names[lang],
      url: `${ORIGIN}${cardPath(card, lang)}`,
    })),
  };

  return (
    <div className="card-meanings-page" lang={lang} dir={strings.dir}>
      <Helmet>
        <html lang={lang} />
        <title>{strings.hubTitle}</title>
        <meta name="description" content={strings.hubDescription} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={`${ORIGIN}${HUB_PATHS.en}`} />
        {MEANING_LANGS.map((code) => (
          <link key={code} rel="alternate" hrefLang={code} href={`${ORIGIN}${HUB_PATHS[code]}`} />
        ))}
        <meta property="og:title" content={strings.hubTitle} />
        <meta property="og:description" content={strings.hubDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />

      <main className="cm-container">
        <nav className="cm-breadcrumbs" aria-label="Breadcrumb">
          <Link to={strings.askPath}>{strings.breadcrumbHome}</Link>
          <span aria-hidden="true"> › </span>
          <span>{strings.breadcrumbHub}</span>
        </nav>

        <nav className="cm-language-switcher" aria-label="Language switcher">
          <span className="cm-language-label">{strings.languageSwitcherLabel}</span>
          <div className="cm-language-links">
            {MEANING_LANGS.map((switchLang) => (
              <Link
                key={switchLang}
                to={HUB_PATHS[switchLang]}
                className={`cm-language-link ${switchLang === lang ? 'is-active' : ''}`}
              >
                {strings.languageNames[switchLang]}
              </Link>
            ))}
          </div>
        </nav>

        <header className="cm-hub-header">
          <h1>{strings.hubHeading}</h1>
          <p>{strings.hubIntro}</p>
        </header>

        {ARCANA_ORDER.map((arcana) => (
          <section className="cm-section" key={arcana}>
            <h2>{strings.arcana[arcana]}</h2>
            <div className="cm-card-grid">
              {CARDS.filter((card) => card.arcana === arcana).map((card) => (
                <Link to={cardPath(card, lang)} className="cm-grid-card" key={card.slug}>
                  <img
                    src={card.image}
                    alt={`${card.names[lang]} — ${strings.arcana[arcana]}`}
                    loading="lazy"
                    width="120"
                    height="211"
                  />
                  <span>{card.names[lang]}</span>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <FeaturePromo variant="page" lang={lang} seed={`hub-${lang}`} />

        <section className="cm-cta">
          <h2>{strings.askCtaHeading}</h2>
          <p>{strings.askCtaText}</p>
          <Link to={strings.askPath} className="cm-cta-button">
            🔮 {strings.askCtaButton}
          </Link>
          <p>
            <Link to={strings.drawPath} className="cm-inline-link">
              {strings.drawCta} →
            </Link>
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CardMeaningsHub;
